import { calcularImpuesto } from "./calcularImpuesto";
import { getDolarValue, convertirAMonedaLocal } from "./exchangeRateService";
import { getImporteFacturable } from "./getImporteFacturable";
import { derivarClaveHardware } from "./lineasFacturacion/resolverLinea";
import { log } from "./log";
import { client } from "./supabase";

// Default de plazo de cobro PROPIO del hardware CAPEX de cartelería: un tramo del split sin
// facturar se proyecta al Backlog en `facturafecha + 30 días` (fijo). Distinto del +60 de
// `sales`/`contratos` (Backlog) y del +45 de "Por cobrar" (facturas_sii_emitidas sin plazocobro).
const DIAS_COBRO_HARDWARE_CAPEX = 30;

// Go-live de la proyección del split de hardware a caja. Los negocios cerrados ANTES de esta
// fecha usaban `detalle_pagos_hardware` como historial de facturación YA saldado (Kennedy, Plaza
// San Damián, etc.) → no se proyectan al Backlog. fecha_cierre manda; si es null se usa created_at.
const FECHA_MINIMA_HARDWARE_CAPEX_CAJA = "2026-08-01";

export const getPipelineCobro = async (pais, monedaLocal = "CLP") => {
  const resultado = [];

  const addDias = (fechaStr, dias) => {
    const d = new Date(`${fechaStr}T12:00:00`);
    d.setDate(d.getDate() + dias);
    return d.toISOString().split("T")[0];
  };

  const primerDiaMes = (mesAnio) => {
    const [mm, yyyy] = mesAnio.split("-");
    return `${yyyy}-${mm}-01`;
  };

  // Último día del mes (mes 1-indexed). new Date(ano, mes, 0) = día 0 del mes siguiente
  // = último día del mes actual. Cartelería se cobra dentro del mes de servicio.
  // Se construye el string a mano para evitar drift de zona horaria de toISOString().
  const ultimoDiaMes = (mes, ano) => {
    const m = parseInt(mes, 10);
    const a = parseInt(ano, 10);
    const dia = new Date(a, m, 0).getDate();
    return `${a}-${String(m).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
  };

  const hoy = new Date().toISOString().split("T")[0];

  const { data: salesData } = await client
    .from("sales")
    .select("id, cliente, importe, descuento_acuerdo_pct, moneda_dolar, mes_factura, ano, tipo, fecha_cobro_estimada")
    .eq("pais", pais)
    .eq("estatus", "100%")
    .eq("eliminada", false)
    .is("facturanumero", null);

  for (const s of salesData || []) {
    if (!s.mes_factura) continue;
    // fechaAuto = cálculo automático SIEMPRE (sin override), para referencia en el modal.
    let fechaAuto;
    if (s.tipo === "p") {
      const [mm, yyyy] = s.mes_factura.split("-");
      const ultimoDia = new Date(parseInt(yyyy, 10), parseInt(mm, 10), 0);
      fechaAuto = addDias(ultimoDia.toISOString().split("T")[0], 60);
    } else {
      const fechaBase = primerDiaMes(s.mes_factura);
      fechaAuto = addDias(fechaBase, 60);
    }
    // Override manual tiene prioridad absoluta — se usa directo sin sumar días.
    const fechaEstimada = s.fecha_cobro_estimada || fechaAuto;
    // Sin filtro por fecha: el Backlog debe mostrar TODAS las ventas 100% sin facturar,
    // incluidas las de fecha proyectada pasada (vencidas). Mismo precedente que "Por cobrar"
    // (regla 10.68): jamás ocultar una pendiente por vencida; se marca en rojo en el render.

    // sales.importe es NETO. El backlog debe reflejar el monto que entrará a "Por cobrar"
    // = neto + IVA/IGV. Excepción: tipo "p" (programmatic/exportación) factura exento.
    const exento = s.tipo === "p";
    // Facturable: el monto a cobrar (y su IVA) sale del importe post-descuento de acuerdo (7.22).
    const { total: importeConIva } = calcularImpuesto(getImporteFacturable(s), pais, exento, !!s.moneda_dolar);
    const importeCLP = s.moneda_dolar
      ? Math.round(importeConIva * (getDolarValue(fechaEstimada, pais) || 1))
      : importeConIva;

    resultado.push({
      id: `pipeline-sales-${s.id}`,
      sale_id: s.id,
      cliente: s.cliente || "—",
      importe: importeConIva,
      importeCLP,
      moneda_dolar: s.moneda_dolar || false,
      fecha_estimada: fechaEstimada,
      fecha_automatica: fechaAuto,
      fecha_override_actual: s.fecha_cobro_estimada || null,
      tiene_override: !!s.fecha_cobro_estimada,
      tipo: "pipeline",
      origen: "sales",
    });
  }

  const { data: contratosData } = await client
    .from("sales_contratos")
    .select("id, cliente, meses, descuento_acuerdo_pct, moneda_dolar, tipo")
    .eq("pais", pais)
    .eq("estatus", "100%")
    .eq("eliminada", false);

  for (const c of contratosData || []) {
    const meses = c.meses || [];
    for (let i = 0; i < meses.length; i++) {
      const mes = meses[i];
      if (mes.facturanumero) continue;
      if (!mes.mes && !mes.facturafecha) continue;
      const fechaBase = mes.facturafecha || primerDiaMes(mes.mes);
      const fechaAuto = addDias(fechaBase, 60);
      // Override manual por cuota tiene prioridad absoluta — se usa directo sin sumar días.
      const fechaEstimada = mes.fecha_cobro_estimada || fechaAuto;
      // Sin filtro por fecha (ver Fuente 1, regla 10.68): las cuotas vencidas también entran al Backlog.

      // mes.importe es NETO por cuota. IVA/IGV salvo contrato tipo "p" (exportación).
      const exento = c.tipo === "p";
      // Facturable por cuota: descuento de acuerdo a nivel contrato (7.22).
      const { total: importeConIva } = calcularImpuesto(
        getImporteFacturable({ importe: mes.importe, descuento_acuerdo_pct: c.descuento_acuerdo_pct }),
        pais,
        exento,
        !!c.moneda_dolar
      );
      const importeCLP = c.moneda_dolar
        ? Math.round(importeConIva * (getDolarValue(fechaEstimada, pais) || 1))
        : importeConIva;

      resultado.push({
        id: `pipeline-contrato-${c.id}-${fechaBase}`,
        contrato_id: c.id,
        cuota_index: i,
        // Mes de servicio de la cuota ("MM-YYYY") — desambigua las varias filas del mismo
        // contrato en la columna REFERENCIA del Backlog (no es la fecha de cobro, que va +60).
        mes: mes.mes || null,
        cliente: c.cliente || "—",
        importe: importeConIva,
        importeCLP,
        moneda_dolar: c.moneda_dolar || false,
        fecha_estimada: fechaEstimada,
        fecha_automatica: fechaAuto,
        fecha_override_actual: mes.fecha_cobro_estimada || null,
        tiene_override: !!mes.fecha_cobro_estimada,
        tipo: "pipeline",
        origen: "contratos",
      });
    }
  }

  const { data: carteleriaData } = await client
    .from("negocios_carteleria")
    .select(
      "id, nombre_proyecto, referencia_id, tipo_vertical, modalidad, " +
        "fee_gestion_unitario, fee_contenidos_unitario, incluye_contenidos, " +
        "contenidos_fecha_inicio, contenidos_fecha_fin, " +
        "cuota_hardware_opex, moneda_gestion, moneda_contenidos, moneda_hardware, " +
        "fecha_inicio, plazo_contrato_meses, " +
        "cantidad_pantallas, meses_facturacion, estado, eliminada, " +
        "incluye_hardware, monto_hardware, detalle_pagos_hardware, fecha_cierre, created_at"
    )
    .eq("pais", pais)
    .eq("eliminada", false)
    // Deuda técnica: negocios_carteleria.estado tiene casing inconsistente en DB
    // ("activo" vs "Activo"). Las filas con schedule de facturación usan "Activo".
    // Se incluyen ambas variantes hasta normalizar el dato en DB y consumidores.
    .in("estado", ["activo", "Activo", "100%"]);

  // Resolución de nombre para la columna CONCEPTO. Los contratos recurrentes (vertical EO/TD)
  // tienen nombre_proyecto = null; el nombre real vive en edificios.edificio (EO) o
  // clientes.nombre (TD), referenciado por referencia_id (mismo patrón que useGestionCalcs.js).
  // Los one-off (vertical OT) sí traen nombre_proyecto. Se hace lookup por ID (regla 8).
  const refEdificios = new Set();
  const refClientes = new Set();
  for (const n of carteleriaData || []) {
    if (n.nombre_proyecto) continue;
    if (n.tipo_vertical === "EO" && n.referencia_id != null) refEdificios.add(n.referencia_id);
    else if (n.tipo_vertical === "TD" && n.referencia_id != null) refClientes.add(n.referencia_id);
  }

  const edificiosMap = {};
  if (refEdificios.size > 0) {
    const { data: edifData } = await client
      .from("edificios")
      .select("id, edificio")
      .in("id", [...refEdificios]);
    for (const e of edifData || []) edificiosMap[e.id] = e.edificio;
  }

  const clientesMap = {};
  if (refClientes.size > 0) {
    const { data: cliData } = await client
      .from("clientes")
      .select("id, nombre")
      .in("id", [...refClientes]);
    for (const c of cliData || []) clientesMap[c.id] = c.nombre;
  }

  const resolverNombreCarteleria = (n) => {
    if (n.nombre_proyecto) return n.nombre_proyecto;
    if (n.tipo_vertical === "EO" && edificiosMap[n.referencia_id]) return edificiosMap[n.referencia_id];
    if (n.tipo_vertical === "TD" && clientesMap[n.referencia_id]) return clientesMap[n.referencia_id];
    return `Cartelería ref ${n.referencia_id}`;
  };

  // Proyección de cartelería recurrente. A diferencia de sales/contratos (que leen cuotas
  // ya cargadas), los contratos de cartelería sólo guardan en meses_facturacion los meses
  // YA facturados; los futuros hay que generarlos. Se replica el patrón de useGestionCalcs.js:
  // horizonte = fecha_inicio + plazo_contrato_meses, y cada mes genera gestión (+ contenidos
  // si aplica) y opex (si el contrato es OPEX).
  //
  // CRÍTICO: gestión, contenidos y opex se convierten a moneda local POR SEPARADO con su
  // propia moneda (moneda_gestion / moneda_contenidos / moneda_hardware) antes de sumar —
  // nunca sumar montos de distinta moneda en crudo (bug histórico UF+CLP). Gestión y
  // contenidos viajan en la MISMA factura SII (ver useGestionCalcs.js), así que se emiten
  // como una sola entry; opex es factura separada.
  const hoyDate = new Date(`${hoy}T00:00:00`);
  const primerDiaMesActual = new Date(hoyDate.getFullYear(), hoyDate.getMonth(), 1);

  // L6 tramo 5c (10.264 — existencia de documento, no de fecha): "mes ya facturado" (excluir del
  // Backlog) deja de decidirse por el folio de texto. Si el folio resuelve a un documento real en
  // el sistema, el puente (`linea_id`) decide en exclusiva; sin documento (Perú, sin SII — 10.265 —
  // o pre-2026 sin Clay) el folio sigue siendo la única fuente posible, sin cambio de comportamiento.
  const foliosGestionOpex = new Set();
  for (const n of carteleriaData || []) {
    const arr =
      typeof n.meses_facturacion === "string" ? JSON.parse(n.meses_facturacion || "[]") : n.meses_facturacion || [];
    for (const mf of arr) if (mf?.facturanumero) foliosGestionOpex.add(String(mf.facturanumero));
  }
  const negocioIdsGestionOpex = (carteleriaData || []).map((n) => String(n.id));
  let lineasGestionOpexRes = { data: [] };
  if (negocioIdsGestionOpex.length > 0) {
    lineasGestionOpexRes = await client
      .from("lineas_facturacion")
      .select("id, negocio_id, tipo, ano, mes")
      .in("negocio_id", negocioIdsGestionOpex)
      .in("tipo", ["gestion", "opex"]);
  }
  let docsGestionOpexRes = { data: [] };
  if (foliosGestionOpex.size > 0) {
    // Mismo criterio de "documento en el sistema" que `siiMap`/`tieneFactura` (10.1): requiere
    // `estado_vinculacion='vinculada'`, no solo que exista la fila (paridad con useVFDataLoading).
    docsGestionOpexRes = await client
      .from("facturas_sii_emitidas")
      .select("folio")
      .eq("pais", pais)
      .eq("estado_vinculacion", "vinculada")
      .in("folio", [...foliosGestionOpex]);
  }
  const lineaIdPorClaveGestionOpex = new Map(
    (lineasGestionOpexRes.data || []).map((l) => [`${l.negocio_id}-${l.tipo}-${l.ano}-${l.mes}`, l.id])
  );
  const lineaIdsGestionOpex = (lineasGestionOpexRes.data || []).map((l) => l.id);
  let lineaIdsConPuenteGestionOpex = new Set();
  if (lineaIdsGestionOpex.length > 0) {
    const { data: puenteGestionOpex } = await client
      .from("facturas_sii_vinculaciones")
      .select("linea_id")
      .in("linea_id", lineaIdsGestionOpex);
    lineaIdsConPuenteGestionOpex = new Set((puenteGestionOpex || []).map((v) => v.linea_id));
  }
  const foliosGestionOpexConDocumento = new Set((docsGestionOpexRes.data || []).map((d) => String(d.folio)));

  for (const n of carteleriaData || []) {
    if (!n.fecha_inicio) continue;
    const plazoMeses = parseInt(n.plazo_contrato_meses, 10) || 0;
    if (plazoMeses === 0) continue;

    const pantallas = parseInt(n.cantidad_pantallas, 10) || 1;
    const nombreCarteleria = resolverNombreCarteleria(n);
    const fechaInicio = new Date(`${n.fecha_inicio}T00:00:00`);
    const fechaFin = new Date(fechaInicio);
    fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
    fechaFin.setDate(fechaFin.getDate() - 1);

    // Ventana propia de contenidos (puede diferir del plazo del contrato base).
    const incluyeContenidos = !!n.incluye_contenidos;
    const contInicio = n.contenidos_fecha_inicio ? new Date(`${n.contenidos_fecha_inicio}T00:00:00`) : null;
    const contFin = n.contenidos_fecha_fin ? new Date(`${n.contenidos_fecha_fin}T00:00:00`) : null;

    // OPEX = cuota de hardware mensual (leasing). TODO: confirmar el determinante de OPEX —
    // se asume modalidad === "OPEX" (verificado en data: sólo esos contratos tienen entries
    // tipo "opex" en meses_facturacion). Se exige además cuota > 0 como guarda defensiva.
    const cuotaOpex = parseFloat(n.cuota_hardware_opex) || 0;
    const esOpex = (n.modalidad || "").toUpperCase() === "OPEX" && cuotaOpex > 0;

    // Meses ya facturados (excluir). Contenidos viaja dentro de la factura de gestión, así
    // que un mes con gestión facturada también cubre contenidos.
    const mesesFact =
      typeof n.meses_facturacion === "string" ? JSON.parse(n.meses_facturacion || "[]") : n.meses_facturacion || [];
    const facturadosGestion = new Set();
    const facturadosOpex = new Set();
    for (const mf of mesesFact) {
      if (!mf.facturanumero) continue;
      const k = `${parseInt(mf.mes, 10)}-${parseInt(mf.ano, 10)}`;
      const folio = String(mf.facturanumero);
      // Sin documento en el sistema, o fuera del alcance 2026 (10.264/10.265): el folio sigue
      // siendo la única fuente, sin excepción — ver nota de tramo 5c en tieneFactura.js (caso real:
      // folios de líneas 2025 coinciden por azar con facturas de dic-2025 cargadas por otro frente).
      let facturado = true;
      if (String(mf.ano) === "2026" && foliosGestionOpexConDocumento.has(folio)) {
        const tipoLinea = mf.tipo === "opex" ? "opex" : "gestion";
        const claveLinea = `${n.id}-${tipoLinea}-${String(mf.ano)}-${String(mf.mes).padStart(2, "0")}`;
        const lineaId = lineaIdPorClaveGestionOpex.get(claveLinea);
        facturado = lineaId != null && lineaIdsConPuenteGestionOpex.has(lineaId);
      }
      if (!facturado) continue;
      if (mf.tipo === "opex") facturadosOpex.add(k);
      else facturadosGestion.add(k);
    }

    // Iterar mes a mes desde el mes actual (o fecha_inicio si es futura) hasta fechaFin.
    const cursor =
      fechaInicio > primerDiaMesActual
        ? new Date(fechaInicio.getFullYear(), fechaInicio.getMonth(), 1)
        : new Date(primerDiaMesActual);
    const finCursor = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), 1);

    while (cursor <= finCursor) {
      const mesNum = cursor.getMonth() + 1; // 1-12
      const anoNum = cursor.getFullYear();
      cursor.setMonth(cursor.getMonth() + 1);

      // fecha_estimada = último día del mes proyectado (cartelería se cobra dentro del
      // mes de servicio: junio → 30/06, julio → 31/07, etc.).
      const fechaEstimada = ultimoDiaMes(mesNum, anoNum);
      if (fechaEstimada <= hoy) continue;

      const key = `${mesNum}-${anoNum}`;
      const slotDate = new Date(anoNum, mesNum - 1, 1);

      // ── Gestión (+ contenidos bundled en la misma factura) ──
      if (!facturadosGestion.has(key)) {
        const gestionBase = (parseFloat(n.fee_gestion_unitario) || 0) * pantallas;
        let importeLocal = convertirAMonedaLocal(gestionBase, n.moneda_gestion, pais, fechaEstimada);
        if (incluyeContenidos) {
          const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
          if (enRango) {
            const contBase = (parseFloat(n.fee_contenidos_unitario) || 0) * pantallas;
            importeLocal += convertirAMonedaLocal(contBase, n.moneda_contenidos, pais, fechaEstimada);
          }
        }
        if (importeLocal > 0) {
          resultado.push({
            id: `pipeline-carteleria-${n.id}-${mesNum}-${anoNum}-gestion`,
            cliente: nombreCarteleria,
            importe: Math.round(importeLocal),
            moneda_dolar: false,
            fecha_estimada: fechaEstimada,
            tipo: "pipeline",
            origen: "carteleria",
          });
        }
      }

      // ── OPEX (hardware mensual, factura separada) ──
      if (esOpex && !facturadosOpex.has(key)) {
        const opexLocal = convertirAMonedaLocal(cuotaOpex * pantallas, n.moneda_hardware, pais, fechaEstimada);
        if (opexLocal > 0) {
          resultado.push({
            id: `pipeline-carteleria-${n.id}-${mesNum}-${anoNum}-opex`,
            cliente: nombreCarteleria,
            importe: Math.round(opexLocal),
            moneda_dolar: false,
            fecha_estimada: fechaEstimada,
            tipo: "pipeline",
            origen: "carteleria",
          });
        }
      }
    }
  }

  // ── Hardware CAPEX: tramos NO facturados del split → Backlog ─────────────────────────────
  // Espejo del patrón de gestión (10.1): cada tramo del split (`detalle_pagos_hardware`) sin
  // facturar se proyecta en `facturafecha + 30 días` (fijo, propio del hardware). El tramo YA
  // facturado lo toma "Por cobrar", así que se EXCLUYE acá. Loop propio: no depende de
  // fecha_inicio/plazo del contrato de gestión (un OT one-off puede tener plazo 0). El lápiz de
  // estas filas queda apagado hasta 2B.
  //
  // TRIPLE CANDADO — un tramo entra al Backlog solo si cumple los tres (evita proyectar el
  // historial viejo que usaba `detalle_pagos_hardware` como registro de facturación ya saldado):
  //   1. GATE FECHA (por negocio): fecha_cierre (o created_at) >= FECHA_MINIMA_HARDWARE_CAPEX_CAJA.
  //   2. GATE PORCENTAJE (por entry): la entry tiene `porcentaje` (solo el split declarado en Fase 1).
  //   3. EXCLUSIÓN FACTURADO por linea_id (L6 tramo 4): no está facturado. Antes matcheaba por
  //      posición (`venta_ref_index`, compartido entre meses_facturacion y detalle_pagos_hardware,
  //      §A.1 del diagnóstico) con desempate por monto — ahora cada tramo tiene su propia línea
  //      estable (`lineas_facturacion`), así que "facturado" es simplemente "existe una fila del
  //      puente con este linea_id"; la colisión de índice desaparece de raíz.
  const parseDetalle = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v !== "string") return null;
    try {
      const p = JSON.parse(v || "[]");
      return Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  };
  const negociosCapexHw = (carteleriaData || []).filter(
    (n) =>
      n.incluye_hardware &&
      (n.modalidad || "").toUpperCase() === "CAPEX" &&
      parseDetalle(n.detalle_pagos_hardware)?.length
  );
  if (negociosCapexHw.length > 0) {
    // linea_id de cada tramo de hardware por CLAVE NATURAL (mismo algoritmo que
    // derivarClaveHardware/nc_lineas_derivadas, 10.1). Una sola query batched para todos los
    // negocios CAPEX-hw del pipeline. `entry_index` se mantiene solo para ubicar el tramo en el
    // array crudo al leer porcentaje/monto/fechas (nunca para decidir "facturado").
    const { data: lineasHwPipe, error: lineasErr } = await client
      .from("lineas_facturacion")
      .select("id, negocio_id, ano, mes, tramo")
      .in(
        "negocio_id",
        negociosCapexHw.map((n) => String(n.id))
      )
      .eq("tipo", "hardware");
    // Candado 3 (L6 tramo 4): "facturado" = existe una fila del puente con este linea_id, sin
    // importar bajo qué venta_tipo se vinculó (gestión y hardware comparten espacio de líneas por
    // negocio, pero cada línea es única — sin colisión posible, a diferencia del índice posicional).
    const { data: vincHw, error: vincErr } = await client
      .from("facturas_sii_vinculaciones")
      .select("linea_id")
      .in("venta_tipo", ["negocios_carteleria", "negocios_carteleria_hw"])
      .not("linea_id", "is", null);
    if (lineasErr || vincErr) {
      // 10.177: sin poder saber qué tramo está facturado, NO proyectar hardware (evitar doble
      // conteo con "Por cobrar"). Se registra el motivo; el resto del pipeline sigue intacto.
      log.error("getPipelineCobro.hardwareCapex", lineasErr || vincErr);
    } else {
      const lineaIdPorClaveHw = new Map(
        (lineasHwPipe || []).map((l) => [`${l.negocio_id}-${l.ano}-${l.mes}-${l.tramo}`, l.id])
      );
      const lineaIdsFacturados = new Set((vincHw || []).map((v) => v.linea_id));

      for (const n of negociosCapexHw) {
        // ── Candado 1 — GATE FECHA: solo negocios nuevos (post go-live del split a caja). Los
        // contratos viejos usaban detalle_pagos_hardware como historial ya saldado.
        const refFecha = n.fecha_cierre || (n.created_at ? String(n.created_at).substring(0, 10) : null);
        if (!refFecha || refFecha < FECHA_MINIMA_HARDWARE_CAPEX_CAJA) continue;

        const detalle = parseDetalle(n.detalle_pagos_hardware) || [];
        const nombreCarteleria = resolverNombreCarteleria(n);
        detalle.forEach((entry, i) => {
          // Fecha de referencia del tramo para ubicarlo en el tiempo: `facturafecha` si ya se facturó/
          // cargó, o `fecha_cobro_estimada` si es un saldo PENDIENTE (sin facturafecha tras separar el
          // 25%). Un tramo confirmado pero aún sin facturar es Backlog VÁLIDO (ingreso futuro esperado),
          // igual que se muestra pendiente en /admin/facturacion; antes se lo excluía por exigir
          // `facturafecha` (mismo patrón de bug que el modal de Proyección y la grilla de Facturación).
          // Sin NINGUNA de las dos fechas no se puede ubicar en el tiempo → se saltea (10.177).
          const fechaRefTramo = entry?.facturafecha || entry?.fecha_cobro_estimada || null;
          if (!entry || typeof entry !== "object" || !fechaRefTramo) {
            log.error(
              "getPipelineCobro.hardwareCapex",
              `tramo hardware ${i} de negocio ${n.id} sin fecha (facturafecha/fecha_cobro_estimada) — omitido`
            );
            return;
          }
          // ── Candado 2 — GATE PORCENTAJE: solo un split declarado en Fase 1 lleva `porcentaje`.
          // Una entry sin porcentaje es historial viejo (ya facturado) → no es Backlog.
          if (entry.porcentaje == null) return;

          const monto = parseFloat(entry.monto) || 0;
          if (monto <= 0) return;

          // linea_id del tramo por su propia clave natural. Un tramo vinculable (fecha + porcentaje +
          // monto ya validados arriba) que no resuelve linea_id es un estado inconsistente — no se
          // puede decidir "facturado" con seguridad, así que se excluye del Backlog (evita doble
          // conteo si en realidad ya está facturado) y se reporta (10.177), nunca en silencio.
          const claveHw = derivarClaveHardware(detalle, i);
          const claveHwKey = claveHw ? `${n.id}-${claveHw.ano}-${claveHw.mes}-${claveHw.tramo}` : null;
          const lineaId = claveHwKey ? (lineaIdPorClaveHw.get(claveHwKey) ?? null) : null;
          if (lineaId == null) {
            log.error("getPipelineCobro.hardwareCapex.linea_id_no_resuelto", {
              negocio_id: n.id,
              entry_index: i,
              claveHw,
            });
            return;
          }

          // ── Candado 3 (L6 tramo 4) — EXCLUSIÓN FACTURADO por linea_id: ya está en "Por cobrar"
          // por su emitida vinculada.
          if (lineaIdsFacturados.has(lineaId)) return;

          // fecha_cobro_estimada = override de caja (lo escribe la Fase 2B); si no, facturafecha + 30.
          const fechaEstimada = entry.fecha_cobro_estimada || addDias(entry.facturafecha, DIAS_COBRO_HARDWARE_CAPEX);
          const importeLocal = convertirAMonedaLocal(monto, n.moneda_hardware, pais, fechaEstimada);
          if (importeLocal <= 0) return;
          resultado.push({
            id: `pipeline-carteleria-${n.id}-hw-${i}`,
            cliente: `${nombreCarteleria} | Hardware ${i + 1}/${detalle.length}`,
            importe: Math.round(importeLocal),
            moneda_dolar: false,
            fecha_estimada: fechaEstimada,
            tipo: "pipeline",
            origen: "carteleria",
            // Contexto para el override de caja (Fase 2B → L6 tramo 3): linea_id es la vía de escritura;
            // entry_index se mantiene para ubicar el tramo en el array crudo hasta el tramo 4b.
            negocio_id: n.id,
            entry_index: i,
            linea_id: lineaId,
          });
        });
      }
    }
  }

  resultado.sort((a, b) => a.fecha_estimada.localeCompare(b.fecha_estimada));

  return resultado;
};
